import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe, Loader2, ExternalLink, BookOpen, Image, Lock, Save, Layout, FileText, Eye, ThumbsUp, Bot,
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { helpCenterAPI, agentsAPI, knowledgeAPI } from '../services/api';
import { HelpCenterSettings as HCSettings, Agent, HelpCenterCollection } from '../types';
import { useToast } from '../contexts/ToastContext';
import usePermissions from '../hooks/usePermissions';

const EMPTY_SETTINGS: HCSettings = {
  display_name: '',
  logo_url: '',
  favicon_url: '',
  brand_color: '#6366f1',
  contact_cta_text: 'Contact Support',
  contact_cta_behavior: 'open_widget',
  contact_email: '',
  author_avatar_enabled: true,
  visibility: 'public',
  visibility_password: '',
  widget_enabled: false,
  show_collections: false,
  selected_collections: [],
  show_uncategorized_articles: true,
  layout_config: {},
};

const TEMPLATE_PRESETS: Record<string, Record<string, any>> = {
  default: {
    template_id: 'default',
    hero_style: 'centered',
    header_style: 'light',
    card_style: 'border',
    layout_columns: 3,
    search_style: 'centered',
    font_size: 'base',
    border_radius: 'md',
    show_article_author: false,
    show_article_updated: true,
    show_breadcrumb: true,
  },
  docs: {
    template_id: 'docs',
    hero_style: 'left',
    header_style: 'light',
    card_style: 'flat',
    layout_columns: 1,
    search_style: 'inline',
    font_size: 'base',
    border_radius: 'sm',
    show_article_author: true,
    show_article_updated: true,
    show_breadcrumb: true,
  },
};

const TEMPLATES = [
  {
    id: 'default',
    label: 'Default',
    description: 'Clean card layout with collections',
    icon: Layout,
    accent: 'from-indigo-500 to-purple-600',
  },
  {
    id: 'docs',
    label: 'Documentation',
    description: 'Sidebar navigation, doc-style',
    icon: FileText,
    accent: 'from-blue-500 to-cyan-600',
  },
];

const slugify = (str: string) =>
  str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const HelpCenterStudio: React.FC = () => {
  const { activeWorkspace } = useWorkspace();
  const { showToast } = useToast();
  // Replaces the inline `user?.roles?.some(r => ['admin','agents_manager'].includes(r))`
  // that used to live inside the no-agents empty state below.
  const { canManageAgents } = usePermissions();

  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [settings, setSettings] = useState<HCSettings>(EMPTY_SETTINGS);
  const [savedSettings, setSavedSettings] = useState<HCSettings>(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  const [hcTotals, setHcTotals] = useState<{ total_views: number; overall_helpfulness_pct: number } | null>(null);
  const [collections, setCollections] = useState<HelpCenterCollection[]>([]);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const workspaceSlug = activeWorkspace?.slug ?? '';
  const selectedAgent = agents.find(a => a.agent_id === selectedAgentId);
  // Stored slug first — it is what the public resolver matches on. The
  // slugify fallback only covers legacy agents with no stored slug, and it
  // mirrors the server's own fallback (help_center_pages.py: exact
  // help_center_slug, else slugified agent_name), so the preview stays honest.
  const agentSlug = selectedAgent?.help_center_slug || slugify(selectedAgent?.config.agent_name ?? '');

  // Debounced settings for preview URL (avoids iframe reload on every keystroke)
  const [debouncedSettings, setDebouncedSettings] = useState(settings);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSettings(settings), 500);
    return () => clearTimeout(timer);
  }, [settings]);

  const previewUrl = useMemo(() => {
    if (!workspaceSlug || !agentSlug) return '';
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
    const base = `${apiUrl}/help/${workspaceSlug}/${agentSlug}`;
    const payload = JSON.stringify({
      brand_color: debouncedSettings.brand_color || '#6366f1',
      display_name: debouncedSettings.display_name,
      logo_url: debouncedSettings.logo_url,
      favicon_url: debouncedSettings.favicon_url,
      layout_config: debouncedSettings.layout_config,
      contact_cta_text: debouncedSettings.contact_cta_text,
      contact_cta_behavior: debouncedSettings.contact_cta_behavior,
      contact_email: debouncedSettings.contact_email,
      widget_enabled: debouncedSettings.widget_enabled,
      show_collections: debouncedSettings.show_collections,
      selected_collections: debouncedSettings.selected_collections,
      show_uncategorized_articles: debouncedSettings.show_uncategorized_articles,
    });
    return `${base}?preview=true&settings=${encodeURIComponent(payload)}`;
  }, [workspaceSlug, agentSlug, debouncedSettings]);

  // Live URL (without preview params) for "Open" button
  const liveUrl = workspaceSlug && agentSlug
    ? `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/help/${workspaceSlug}/${agentSlug}`
    : '';

  const isDirty = JSON.stringify(settings) !== JSON.stringify(savedSettings);

  // Fetch agents on mount
  useEffect(() => {
    agentsAPI.list()
      .then(res => {
        const list: Agent[] = res.data ?? [];
        setAgents(list);
        if (list.length > 0) setSelectedAgentId(list[0].agent_id);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Fetch settings, collections + analytics when agent changes
  useEffect(() => {
    if (!activeWorkspace?.id || !selectedAgentId) return;
    fetchSettings();
    helpCenterAPI.getCollections(selectedAgentId)
      .then(res => setCollections(res.data ?? []))
      .catch(console.error);
    knowledgeAPI.analytics(selectedAgentId)
      .then(res => setHcTotals(res.data.totals))
      .catch(() => setHcTotals(null));
  }, [activeWorkspace?.id, selectedAgentId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await helpCenterAPI.getSettings(selectedAgentId);
      const merged = { ...EMPTY_SETTINGS, ...res.data, agent_id: selectedAgentId };
      setSettings(merged);
      setSavedSettings(merged);
    } catch {
      const defaults = { ...EMPTY_SETTINGS, agent_id: selectedAgentId };
      setSettings(defaults);
      setSavedSettings(defaults);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await helpCenterAPI.updateSettings({ ...settings, agent_id: selectedAgentId });
      const merged = { ...settings, ...res.data };
      setSettings(merged);
      setSavedSettings(merged);
      showToast('Settings saved', 'success');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const CTA_DEFAULTS: Record<string, string> = { open_widget: 'Chat with us', email_link: 'Contact Us' };

  const handleFieldChange = useCallback(<K extends keyof HCSettings>(key: K, value: HCSettings[K]) => {
    if (key === 'widget_enabled' && value === false) {
      setSettings(prev => {
        if (prev.contact_cta_behavior !== 'open_widget') return { ...prev, widget_enabled: false };
        return { ...prev, widget_enabled: false, contact_cta_behavior: 'email_link' as const, contact_cta_text: CTA_DEFAULTS.email_link };
      });
      return;
    }
    if (key === 'contact_cta_behavior') {
      const newBehavior = value as string;
      setSettings(prev => ({
        ...prev,
        contact_cta_behavior: newBehavior as HCSettings['contact_cta_behavior'],
        contact_cta_text: CTA_DEFAULTS[newBehavior],
      }));
      return;
    }
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleLayoutConfigChange = useCallback((key: string, value: any) => {
    setSettings(prev => ({ ...prev, layout_config: { ...prev.layout_config, [key]: value } }));
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>, purpose: 'logo' | 'favicon') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const setter = purpose === 'logo' ? setUploadingLogo : setUploadingFavicon;
    setter(true);
    try {
      const res = await helpCenterAPI.uploadAsset(file, purpose);
      const field = purpose === 'logo' ? 'logo_url' : 'favicon_url';
      setSettings(prev => ({ ...prev, [field]: res.data.url }));
      showToast(`${purpose === 'logo' ? 'Logo' : 'Favicon'} uploaded`, 'success');
    } catch {
      showToast('Upload failed', 'error');
    } finally {
      setter(false);
      e.target.value = '';
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const preset = TEMPLATE_PRESETS[templateId];
    if (preset) {
      setSettings(prev => ({ ...prev, layout_config: { ...prev.layout_config, ...preset } }));
    }
  };

  if (!activeWorkspace?.slug) {
    return (
      <div className="p-6">
        <div className="glass rounded-2xl p-12 text-center">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No workspace selected.</p>
        </div>
      </div>
    );
  }

  if (loading && agents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!loading && agents.length === 0) {
    // Hoisted to the hook above — this legacy expression carried no owner
    // term; usePermissions restores one once the RBAC flag is on.
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100dvh - 8rem)' }}>
        <div className="glass rounded-2xl p-12 text-center max-w-md">
          <Bot className="w-14 h-14 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No AI Agents yet</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Create an AI Agent first to set up your Help Center.
          </p>
          {canManageAgents && (
            <Link
              to="/agents"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 transition-colors"
            >
              <Bot className="w-4 h-4" />
              Go to AI Agents
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="-m-6 flex flex-col overflow-hidden" style={{ height: 'calc(100dvh - 4rem)' }}>

      {/* ── Header ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 glass">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Globe className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate hidden sm:block">
            Help Center
          </span>

          {/* Agent selector */}
          {agents.length > 0 && (
            <select
              value={selectedAgentId}
              onChange={e => setSelectedAgentId(e.target.value)}
              className="ml-2 px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {agents.map(a => (
                <option key={a.agent_id} value={a.agent_id}>{a.config.agent_name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Open live */}
          {liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Open</span>
            </a>
          )}

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className={`relative inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium text-white transition-colors ${
              isDirty
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700'
                : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
            } disabled:opacity-60`}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
            {isDirty && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-white dark:border-gray-900" />
            )}
          </button>
        </div>
      </div>

      {/* ── Analytics summary bar ── */}
      <div className="flex-shrink-0 flex items-center gap-4 px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <Eye className="w-3 h-3" />
          {(hcTotals?.total_views ?? 0).toLocaleString()} views
        </span>
        <span className="inline-flex items-center gap-1">
          <ThumbsUp className="w-3 h-3" />
          {hcTotals && hcTotals.overall_helpfulness_pct != null && hcTotals.overall_helpfulness_pct > 0
            ? `${hcTotals.overall_helpfulness_pct.toFixed(1)}% helpfulness`
            : 'No feedback yet'}
        </span>
      </div>

      {/* ── Main content: Settings + Preview ── */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">

        {/* ── Left: Settings Panel ── */}
        <div className="w-full max-h-[50vh] lg:max-h-none lg:w-[380px] flex-shrink-0 overflow-y-auto border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-6">

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : (
            <>
              {/* Template */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Template</h3>
                <div className="grid grid-cols-2 gap-3">
                  {TEMPLATES.map(t => {
                    const Icon = t.icon;
                    const active = settings.layout_config?.template_id === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => handleTemplateSelect(t.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors ${
                          active
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${t.accent} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className={`text-xs font-medium ${active ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                          {t.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Branding */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Branding</h3>
                <div className="space-y-4">
                  {/* Display name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Name</label>
                    <input
                      type="text"
                      value={settings.display_name ?? ''}
                      onChange={e => handleFieldChange('display_name', e.target.value)}
                      placeholder={activeWorkspace?.name ?? 'Help Center'}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Logo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logo</label>
                    <div className="flex items-center gap-3">
                      {settings.logo_url && (
                        <img src={settings.logo_url} alt="Logo" className="h-10 w-auto rounded-lg border border-gray-200 dark:border-gray-700 object-contain" />
                      )}
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                        {uploadingLogo ? 'Uploading...' : 'Upload'}
                      </button>
                      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e, 'logo')} />
                    </div>
                  </div>

                  {/* Favicon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Favicon</label>
                    <div className="flex items-center gap-3">
                      {settings.favicon_url && (
                        <img src={settings.favicon_url} alt="Favicon" className="h-8 w-8 rounded border border-gray-200 dark:border-gray-700 object-contain" />
                      )}
                      <button
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={uploadingFavicon}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {uploadingFavicon ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                        {uploadingFavicon ? 'Uploading...' : 'Upload'}
                      </button>
                      <input ref={faviconInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e, 'favicon')} />
                    </div>
                  </div>

                  {/* Brand color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={settings.brand_color ?? '#6366f1'}
                        onChange={e => handleFieldChange('brand_color', e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer"
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {settings.brand_color ?? '#6366f1'}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact CTA */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Contact CTA</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA Text</label>
                    <input
                      type="text"
                      value={settings.contact_cta_text}
                      onChange={e => handleFieldChange('contact_cta_text', e.target.value)}
                      placeholder="Contact Support"
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">CTA Behavior</label>
                    <div className="flex gap-2">
                      {(['open_widget', 'email_link'] as const).map(opt => {
                        const disabled = !settings.widget_enabled && opt === 'open_widget';
                        return (
                          <label
                            key={opt}
                            className={`flex-1 flex flex-col items-center justify-center gap-1 px-3 py-2.5 rounded-xl border transition-colors text-sm font-medium ${
                              disabled
                                ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                                : settings.contact_cta_behavior === opt
                                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 cursor-pointer'
                                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-indigo-300 cursor-pointer'
                            }`}
                          >
                            <input
                              type="radio"
                              name="cta_behavior"
                              value={opt}
                              checked={settings.contact_cta_behavior === opt}
                              onChange={() => handleFieldChange('contact_cta_behavior', opt)}
                              disabled={disabled}
                              className="sr-only"
                            />
                            {opt === 'open_widget' ? 'Open Widget' : 'Email Link'}
                            {disabled && (
                              <span className="text-[10px] text-gray-400 dark:text-gray-500 font-normal">Enable widget first</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {settings.contact_cta_behavior === 'email_link' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                      <input
                        type="email"
                        value={settings.contact_email ?? ''}
                        onChange={e => handleFieldChange('contact_email', e.target.value)}
                        placeholder="support@example.com"
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}
                </div>
              </section>

              {/* Access Control */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Access Control</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Visibility</label>
                    <select
                      value={settings.visibility}
                      onChange={e => handleFieldChange('visibility', e.target.value as HCSettings['visibility'])}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="public">Public</option>
                      <option value="password_protected">Password Protected</option>
                      <option value="private">Private (Team Only)</option>
                    </select>
                  </div>

                  {settings.visibility === 'password_protected' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                      <input
                        type="password"
                        value={settings.visibility_password ?? ''}
                        onChange={e => handleFieldChange('visibility_password', e.target.value)}
                        placeholder="Set a password"
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}

                  {settings.visibility === 'private' && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-xs text-red-700 dark:text-red-300">
                      <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>This Help Center will not be indexed by search engines and is only accessible to workspace members.</span>
                    </div>
                  )}
                </div>
              </section>

              {/* AI Agent Widget */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">AI Agent Widget</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Show AI Agent chat widget</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Embed the {selectedAgent?.config.agent_name ?? 'agent'} chat widget on this Help Center
                    </p>
                  </div>
                  <button
                    onClick={() => handleFieldChange('widget_enabled', !settings.widget_enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.widget_enabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        settings.widget_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </section>

              {/* Collections */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Collections</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Show Collections</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Group articles by collection on your Help Center page
                    </p>
                  </div>
                  <button
                    onClick={() => handleFieldChange('show_collections', !settings.show_collections)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.show_collections ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        settings.show_collections ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Selected collections picker */}
                {settings.show_collections && collections.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Display collections {(!settings.selected_collections || settings.selected_collections.length === 0) && <span className="text-gray-400 dark:text-gray-500">(all shown by default)</span>}
                    </p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {collections.map(col => {
                        const selected = (settings.selected_collections ?? []).includes(col.id);
                        return (
                          <label
                            key={col.id}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                              selected
                                ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700'
                                : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => {
                                const current = settings.selected_collections ?? [];
                                const next = selected
                                  ? current.filter(id => id !== col.id)
                                  : [...current, col.id];
                                handleFieldChange('selected_collections', next);
                              }}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-base">{col.emoji ?? '📚'}</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{col.name}</span>
                            {col.article_count !== undefined && (
                              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                {col.article_count}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Show Other Articles (uncategorized) toggle */}
                {settings.show_collections && (
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Show Other Articles</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Display articles not assigned to any collection
                      </p>
                    </div>
                    <button
                      onClick={() => handleFieldChange('show_uncategorized_articles', !(settings.show_uncategorized_articles ?? true))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        (settings.show_uncategorized_articles ?? true) ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          (settings.show_uncategorized_articles ?? true) ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}
              </section>

              {/* Brand Backgrounds */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Brand Backgrounds</h3>
                <div className="space-y-3">
                  {([
                    { key: 'header_brand_bg', label: 'Header Background', desc: 'Use brand color as header background', fontKey: 'header_font_color' },
                    { key: 'cards_brand_bg', label: 'Cards Background', desc: 'Use brand color as article cards grid background', fontKey: null },
                    { key: 'footer_brand_bg', label: 'Footer Background', desc: 'Use brand color as footer background', fontKey: 'footer_font_color' },
                  ] as const).map(item => (
                    <div key={item.key}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.desc}</p>
                        </div>
                        <button
                          onClick={() => handleLayoutConfigChange(item.key, !settings.layout_config?.[item.key])}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            settings.layout_config?.[item.key] ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              settings.layout_config?.[item.key] ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      {item.fontKey && settings.layout_config?.[item.key] && (
                        <div className="flex items-center gap-2 mt-2 ml-1">
                          <label className="text-xs text-gray-500 dark:text-gray-400">Font Color</label>
                          <input
                            type="color"
                            value={(settings.layout_config?.[item.fontKey] as string) ?? '#ffffff'}
                            onChange={e => handleLayoutConfigChange(item.fontKey!, e.target.value)}
                            className="w-7 h-7 rounded border border-gray-200 dark:border-gray-700 cursor-pointer"
                          />
                          <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                            {(settings.layout_config?.[item.fontKey] as string) ?? '#ffffff'}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Knowledge Hub link */}
              <div className="pt-2 text-center">
                <Link
                  to="/knowledge"
                  className="inline-flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  <BookOpen className="w-3 h-3" />
                  Manage articles in Knowledge Hub
                </Link>
              </div>
            </>
          )}
        </div>

        {/* ── Right: Live Preview ── */}
        <div className="flex-1 min-h-[400px] lg:min-h-0 relative bg-gray-100 dark:bg-gray-900 overflow-hidden">
          {iframeLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
            </div>
          )}
          {previewUrl ? (
            <iframe
              key={previewUrl}
              src={previewUrl}
              className="w-full h-full border-0"
              onLoad={() => setIframeLoading(false)}
              title="Help Center Preview"
              // Permissions Policy: the embedded widget renders the VoicePlayground
              // which calls `navigator.mediaDevices.getUserMedia({audio:true})`.
              // Without `allow="microphone"` the browser denies the request with
              // `NotAllowedError: Permission denied` and emits the violation
              // "[Violation] Permissions policy violation: microphone is not
              // allowed in this document". Mirrors Playground.tsx:404 and
              // public/widget.js iframe.setAttribute('allow', …).
              allow="microphone; camera; autoplay; clipboard-write; fullscreen"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-600 text-sm">
              No help center URL configured
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenterStudio;
